import React from 'react';
import { ChatBubbleLeftRightIcon, UserGroupIcon, CheckCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

// Percent formatter that emits a real +/− sign (U+2212) — so trend direction is
// derived from the numeric value, not by sniffing a hand-written string.
const pctFmt = new Intl.NumberFormat('en-US', {
  style: 'percent',
  signDisplay: 'exceptZero',
  maximumFractionDigits: 0,
});

/**
 * KPISummary component displays the top-level KPI tiles for the dashboard
 * with the updated labels for the RAG chatbot system.
 */
const KPISummary = ({ data }) => {
  const {
    activeConversations = 0,
    activeUsers = 0,
    resolvedQueries = 0,
    pendingResponses = 0,
  } = data || {};

  // change is a signed fraction (0.15 = +15%). invert=true means "down is good"
  // (e.g. fewer pending responses), so the good/bad color flips.
  const kpis = [
    {
      title: 'Active Conversations',
      value: activeConversations,
      change: 0.15,
      icon: <ChatBubbleLeftRightIcon className="h-6 w-6 text-blue-400" aria-hidden="true" />,
      bgColor: 'bg-blue-900/20',
    },
    {
      title: 'Active Users',
      value: activeUsers,
      change: -0.02,
      icon: <UserGroupIcon className="h-6 w-6 text-green-400" aria-hidden="true" />,
      bgColor: 'bg-green-900/20',
    },
    {
      title: 'Resolved Queries',
      value: resolvedQueries,
      change: 0.1,
      icon: <CheckCircleIcon className="h-6 w-6 text-purple-400" aria-hidden="true" />,
      bgColor: 'bg-purple-900/20',
    },
    {
      title: 'Pending Responses',
      value: pendingResponses,
      change: 0,
      invert: true,
      icon: <ClockIcon className="h-6 w-6 text-orange-400" aria-hidden="true" />,
      bgColor: 'bg-orange-900/20',
    },
  ];

  const changeColor = (change, invert) => {
    if (!change) return 'text-gray-400';
    const good = invert ? change < 0 : change > 0;
    return good ? 'text-green-400' : 'text-red-400';
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {kpis.map((kpi, index) => (
        <div key={index} className={`rounded-xl p-5 ${kpi.bgColor} border border-gray-700/60 shadow-sm transition-transform duration-150 hover:-translate-y-1`}>
          <div className="flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 rounded-full bg-gray-800">
                {kpi.icon}
              </div>
              <span className={`text-xs tabular-nums ${changeColor(kpi.change, kpi.invert)}`}>
                {pctFmt.format(kpi.change)}
              </span>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-400">{kpi.title}</h3>
              <p className="text-3xl font-bold mt-1 tabular-nums">{kpi.value}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default KPISummary;
